import { CountUp } from "../../../effects/CountUp";
import { askAboutAndReveal } from "../../../state/chatActions";
import type { Portfolio } from "../../../data/types";
import { fmtEur, fmtGbp } from "./format";
import styles from "./RetentionForecast.module.css";

interface RetentionForecastProps {
  portfolio: Portfolio;
}

export function RetentionForecast({ portfolio }: RetentionForecastProps) {
  const terminated = portfolio.merchants.filter((m) => m.status === "Terminated");
  const warn = portfolio.merchants.filter((m) => m.status === "Warning");
  const managedVol = portfolio.merchants
    .filter((m) => m.status !== "Terminated")
    .reduce((s, m) => s + m.totalVolume, 0);
  const lossedVol = terminated.reduce((s, m) => s + m.totalVolume, 0);

  return (
    <div className={styles.card}>
      <header className={styles.header}>
        <span className={styles.title}>retention forecast</span>
        <span className={styles.muted}>ytd / week</span>
      </header>

      <div className={styles.headline}>
        <button
          type="button"
          className={styles.block}
          onClick={() =>
            askAboutAndReveal(
              `break down the ${fmtEur(portfolio.weekSecuredEur)} secured this week — which interventions contributed and on which merchants? ${portfolio.interventionsWeek.accepted} of ${portfolio.interventionsWeek.fired} accepted.`
            )
          }
        >
          <span className={styles.label}>secured this week</span>
          <span className={styles.big}>
            <CountUp
              value={portfolio.weekSecuredEur}
              format={(n) => fmtEur(n)}
              durationMs={900}
            />
          </span>
          <span className={styles.sub}>
            {portfolio.interventionsWeek.accepted}/
            {portfolio.interventionsWeek.fired} interventions accepted
          </span>
        </button>
        <button
          type="button"
          className={styles.block}
          onClick={() =>
            askAboutAndReveal(
              `walk me through the ${fmtEur(portfolio.ytdSecuredEur)} ytd secured number. how is it calculated, and which merchants account for the biggest chunks?`
            )
          }
        >
          <span className={styles.label}>secured ytd</span>
          <span className={styles.big}>
            <CountUp
              value={portfolio.ytdSecuredEur}
              format={(n) => fmtEur(n)}
              durationMs={1100}
            />
          </span>
          <span className={styles.sub}>
            tc40 suppressed (wk){" "}
            <span className={styles.pill}>{fmtGbp(portfolio.tc40SuppressedGbpWeek)}</span>
          </span>
        </button>
      </div>

      <div className={styles.split}>
        <button
          type="button"
          className={styles.splitLine}
          onClick={() =>
            askAboutAndReveal(
              `what does the ${fmtEur(managedVol)} managed trajectory line represent? how is that split across merchants and mccs?`
            )
          }
        >
          <span className={styles.legendDot} data-tone="good" />
          <span className={styles.splitLabel}>managed trajectory</span>
          <span className={styles.splitValue}>{fmtEur(managedVol)}</span>
        </button>
        <button
          type="button"
          className={styles.splitLine}
          onClick={() =>
            askAboutAndReveal(
              `which merchants did we lose (${fmtEur(lossedVol)} total)? when did they terminate, why, and could earlier intervention have saved them?`
            )
          }
        >
          <span className={styles.legendDot} data-tone="bad" />
          <span className={styles.splitLabel}>lost to termination</span>
          <span className={styles.splitValue}>{fmtEur(lossedVol)}</span>
        </button>
        <button
          type="button"
          className={styles.splitLine}
          onClick={() =>
            askAboutAndReveal(
              `we have ${warn.length} merchants on the watchlist. list them and tell me which need the most urgent action.`,
              "intelligence",
              { kind: "cohort", filter: "Warning" }
            )
          }
        >
          <span className={styles.legendDot} data-tone="warn" />
          <span className={styles.splitLabel}>on watchlist</span>
          <span className={styles.splitValue}>{warn.length} merchants</span>
        </button>
      </div>
    </div>
  );
}
